// TurningSixtyFiveCountdown — blueprint module #1 Client Dashboard,
// specifically the "turning 65 countdown" first-class field the
// blueprint calls out.
//
// Reads client DOB from BOTH sources:
//   1. Direct dob prop (passed in when known — HealthClientProfileModal
//      has it on hand from the primary health_policies row).
//   2. Falls back to querying health_policies.client_dob (RLS-scoped).
//
// Renders one of three states:
//   - Not yet 65: "Turns 65 in Nd · Medicare IEP starts YYYY-MM"
//     Amber if within 6 months, sky if farther out.
//   - Just turned 65 (last 90d): "Aged into Medicare Nd ago" (green)
//   - Already 65+ for a while: "Medicare eligible" (subtle gray chip)
//   - No DOB on file: renders nothing.

import React from 'react';
import { supabase } from '../lib/supabase';

// Compute months / days until 65 from an ISO date string ("YYYY-MM-DD").
// Uses UTC boundaries so tz doesn't wobble the countdown by 1 day.
function computeCountdown(dob) {
  if (!dob) return null;
  const d = new Date(dob + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return null;
  const sixtyFifth = new Date(Date.UTC(d.getUTCFullYear() + 65, d.getUTCMonth(), d.getUTCDate()));
  const now = new Date();
  const days = Math.round((sixtyFifth.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  const months = Math.round(days / 30.4);
  // Medicare Initial Enrollment Period: 3 months before turning 65,
  // through the birthday month, through 3 months after. UI hints
  // "IEP starts" 3 months before the 65th birthday.
  const iepStart = new Date(Date.UTC(d.getUTCFullYear() + 65, d.getUTCMonth() - 3, 1));
  return { days, months, sixtyFifth, iepStart };
}

function fmtMonth(d) {
  return d.toLocaleString(undefined, { month: 'short', year: 'numeric' });
}

export default function TurningSixtyFiveCountdown({ clientId, ownerId, dob: dobProp }) {
  const [dob, setDob] = React.useState(dobProp || null);

  React.useEffect(() => {
    if (dobProp) { setDob(dobProp); return; }
    if (!clientId || !ownerId) return;
    let cancelled = false;
    (async () => {
      try {
        // Primary: health_policies stores DOB per policy per client.
        const { data } = await supabase
          .from('health_policies')
          .select('client_dob')
          .eq('owner_id', ownerId)
          .eq('client_id', clientId)
          .not('client_dob', 'is', null)
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (data?.client_dob) { setDob(data.client_dob); return; }
        // Fallback: clients table has dob (migration 026/040).
        const { data: cli } = await supabase
          .from('clients')
          .select('dob')
          .eq('id', clientId)
          .maybeSingle();
        if (cancelled) return;
        if (cli?.dob) setDob(cli.dob);
      } catch { /* dob just stays null; component renders nothing */ }
    })();
    return () => { cancelled = true; };
  }, [clientId, ownerId, dobProp]);

  const cd = computeCountdown(dob);
  if (!cd) return null;

  const { days, months, sixtyFifth, iepStart } = cd;

  // Post-65 by more than 90 days — subtle "Medicare eligible" chip.
  if (days < -90) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
        <span>◐</span> Medicare eligible
      </span>
    );
  }

  // Just aged into Medicare (last 90 days) — bright green celebration chip.
  if (days <= 0) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
        <span>✓</span> Aged in — {Math.abs(days)}d ago
      </span>
    );
  }

  // Within 6 months (IEP window is 3mo before + birthday month + 3mo after).
  const inIepWindow = months <= 6;
  const tone = inIepWindow
    ? 'bg-amber-50 border-amber-300 text-amber-900'
    : 'bg-sky-50 border-sky-300 text-sky-900';
  const countdownLabel = days <= 60 ? `${days}d` : `${months}mo`;
  const iepLabel = inIepWindow
    ? `IEP open · through ${fmtMonth(new Date(Date.UTC(sixtyFifth.getUTCFullYear(), sixtyFifth.getUTCMonth() + 3, 1)))}`
    : `IEP starts ${fmtMonth(iepStart)}`;

  return (
    <div className={`inline-flex items-center gap-2 text-[11px] px-2.5 py-1.5 rounded-lg border ${tone}`}>
      <span className="text-base leading-none">🎂</span>
      <div className="leading-tight">
        <div className="font-bold uppercase tracking-wide text-[10px]">Turns 65 in {countdownLabel}</div>
        <div className="text-[10px] opacity-80 mt-0.5">{iepLabel}</div>
      </div>
    </div>
  );
}
